import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useJob } from '../hooks/useJob';
import { useUpdateJobStatus } from '../hooks/useUpdateJobStatus';
import { JobHeader } from '../components/JobHeader';
import { JobCostOverview } from '../components/JobCostOverview';
import { JobEditForm } from '../components/JobEditForm';
import { JobStatusChangeDialog } from '../components/JobStatusChangeDialog';
import type { JobStatus } from '../types';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobId = id ? parseInt(id, 10) : undefined;

  const { data: job, isLoading, error, refetch } = useJob(jobId);
  usePageTitle(job ? `Job #${job.jobNumber} - ${job.lead.customerName}` : 'Job Details');
  const { updateStatus, isUpdating } = useUpdateJobStatus();

  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState<JobStatus>('Sold');

  const handleStatusChange = (newStatus: JobStatus) => {
    setTargetStatus(newStatus);
    setIsStatusDialogOpen(true);
  };

  const handleStatusConfirm = async (
    closingComments?: string,
    completedDate?: string,
    closedDate?: string
  ) => {
    if (!job) return;

    const result = await updateStatus(
      job.id,
      targetStatus,
      closingComments,
      completedDate,
      closedDate
    );

    if (result) {
      setIsStatusDialogOpen(false);
      refetch();
    }
  };

  const handleEditSave = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" label="Loading job..." />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="container mx-auto p-6">
        <ErrorAlert error={error || 'Job not found'} className="rounded-lg p-4" />
        <button
          onClick={() => navigate('/jobs')}
          className="mt-4 text-secondary hover:underline"
        >
          ← Back to Jobs
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto p-6">
      {/* Header */}
      <JobHeader
        job={job}
        onStatusChange={handleStatusChange}
        onEditClick={() => setIsEditFormOpen(true)}
      />

      {/* Job Cost Overview (Overview tab removed — see plan Phase 3.1) */}
      <div className="bg-background min-h-[400px]">
        <JobCostOverview jobId={job.id} job={job} onJobUpdate={refetch} />
      </div>

      {/* Edit Form Dialog */}
      <JobEditForm
        job={job}
        isOpen={isEditFormOpen}
        onClose={() => setIsEditFormOpen(false)}
        onSave={handleEditSave}
      />

      {/* Status Change Dialog */}
      <JobStatusChangeDialog
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        onConfirm={handleStatusConfirm}
        targetStatus={targetStatus}
        isLoading={isUpdating}
      />
    </div>
  );
}
